import { SourcingPostingRunningBatch } from 'api-clients/monolith';
import { Loader2 } from 'lucide-react';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { ChevronRightIcon } from '@/components/icons/chevron-right';
import { Banner } from '@/components/ui/banner';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import {
  FOCUS_RING,
  usePostingCountValues,
} from '@/components/pages/dashboard/running-posting-status';
import { useOpeningDetails } from '@/contexts/opening-context';
import { useNavigationPaths } from '@/hooks/use-navigation-paths';
import { useRunningPostingBatches } from '@/hooks/use-running-posting-batches';
import { reportBoundaryError } from '@/utils/sentry';

function RunningBanner({
  batch,
  postJobPath,
}: {
  batch: SourcingPostingRunningBatch;
  postJobPath: string;
}) {
  const values = usePostingCountValues(batch);
  return (
    <Banner
      color="brand"
      className="flex-wrap shadow-none"
      icon={
        <Loader2
          aria-hidden="true"
          className="text-fx-brand size-5 shrink-0 animate-spin motion-reduce:animate-none"
        />
      }
      description={
        <div role="status">
          <Typography variant="body1">
            <FormattedMessage
              defaultMessage="Posting {jobs} for {openings}: {done} done. They'll appear below once posted."
              description="Banner on an opening's Channels tab while a bulk job post that includes it runs in the background; {jobs} reads '20 jobs', {openings} '5 openings', {done} is how many postings have settled"
              values={values ?? {}}
            />
          </Typography>
          {batch.started_by.name && (
            <Typography variant="small1" className="text-fx-neutral-600">
              <FormattedMessage
                defaultMessage="Started by {name}"
                description="Second line of the running bulk-post banner naming the person who pressed Post"
                values={{ name: batch.started_by.name }}
              />
            </Typography>
          )}
        </div>
      }
      button={
        <Button variant="secondary" className={FOCUS_RING} asChild>
          <Link to={`${postJobPath}/${batch.id}`}>
            <FormattedMessage
              defaultMessage="View progress"
              description="Button opening the page that lists every posting of a running bulk job post"
            />
            <ChevronRightIcon className="text-fx-neutral-900" />
          </Link>
        </Button>
      }
    />
  );
}

function RunningPostingBannerContent() {
  const { opening } = useOpeningDetails();
  const { postJobPath } = useNavigationPaths();
  const { batches } = useRunningPostingBatches({ openingId: opening.id });

  return batches.map(batch => (
    <RunningBanner key={batch.id} batch={batch} postJobPath={postJobPath} />
  ));
}

/** One brand banner per running bulk post that includes this opening. */
export function RunningPostingBanner() {
  return (
    <ErrorBoundary
      onError={reportBoundaryError('channels.running-posting-banner')}
      fallback={null}
    >
      <Suspense fallback={null}>
        <RunningPostingBannerContent />
      </Suspense>
    </ErrorBoundary>
  );
}
